///
/// EarthBrowser
///
/// Created by Matt Giger on 11/24/2023
/// Copyright (c) 2023 Geodata Labs
///

import { type ActivityBase } from "@stovokor/engine/ActivityBase"
import { renderRequestVersion, requestRender } from "@stovokor/engine/RenderRequest"
import { UIViewport } from "@stovokor/engine/UIViewport"
import { WorldViewport } from "@stovokor/engine/WorldViewport"
import { time } from "@stovokor/math/time"
import { StovokorContext } from "@stovokor/StovokorContext"
import { TaskManager } from "@stovokor/tasks/TaskManager"

/**
 * The main class for the engine.
 * It is responsible for initializing WebGL, context, window manager, and dispatcher.
 * and running the main loop. It contains a world viewport, interface viewport, clock, task manager,
 * activity manager, and event dispatcher.
 */
export class Stovokor {
	public readonly context: StovokorContext

	public world: WorldViewport
	public labels: UIViewport
	public interface: UIViewport
	public taskManager = new TaskManager(this)
	public baseActivity!: ActivityBase

	// protected canvas: HTMLCanvasElement
	protected canvasWidth: number = 0
	protected canvasHeight: number = 0
	private maxDimensionCache?: number

	private running: boolean = true
	/** Frames drawn per second (ticks that skipped drawing don't count) */
	public frameRate: number = 0

	/**
	 * Frame pacing. The loop ticks every animation frame (tasks and the
	 * focus/camera update always run) but only draws when something asked
	 * for it: input, camera motion, resizes, texture uploads, pane
	 * re-rasterization, scene-graph or transform changes, or an explicit
	 * requestRender(). For `idleDelay` seconds after a request every tick
	 * draws (so fades that follow an event stay smooth); after that the
	 * scene is considered idle and draws at most `idleFrameRate` per
	 * second. That floor keeps things the engine can't see changing
	 * (material uniforms animated by frame tasks, wall clock shader time)
	 * moving. Set 0 for pure on-demand rendering once every animated
	 * element requests its own redraws, or Infinity to draw every tick as
	 * before.
	 */
	public idleFrameRate: number = 30
	public idleDelay: number = 0.5

	private lastRequestTime: number = performance.now() * 0.001
	private lastRenderTime: number = -Infinity
	private seenRequestVersion: number = -1
	private renderedFrames: number = 0

	// Canvas size is measured when something reports a change (ResizeObserver,
	// window resize, pixel-ratio media query) and, as a safety net, once a
	// second; getBoundingClientRect every frame forced a layout each tick
	private lastResizeCheck: number = -Infinity
	private pixelRatioQuery?: MediaQueryList
	private readonly pixelRatioChanged = (): void => {
		this.context.canvasResized = true
		this.watchPixelRatio()
	}

	/**
	 * Optional hook invoked after all viewports render, while the frame's
	 * drawing buffer is still valid (contexts are typically created without
	 * preserveDrawingBuffer, so this is the only safe place to capture the
	 * canvas contents, e.g. for thumbnails)
	 */
	public onPostRender?: () => void

	private frameId: number = 0
	private frameCount: number = 0
	private lastTimestamp: number = 0
	private lastRenderedFrames: number = 0

	constructor(context: StovokorContext) {
		this.context = context
		this.world = new WorldViewport(context)
		this.labels = new UIViewport(context)
		this.labels.clearDepthOnRender = false
		this.interface = new UIViewport(context)

		// Initialize FocusManager with viewport handlers
		// Label viewport preserves world depth for occlusion testing
		// UI viewport is checked first for gesture hit testing (2D panes)
		// World viewport is the fallback for 3D scene
		context.focusManager.setLabelViewportHandler(this.labels)
		context.focusManager.setUIViewportHandler(this.interface)
		context.focusManager.setViewportHandler(this.world)

		this.watchPixelRatio()

		const gl = context.gl
		gl.enable(WebGL2RenderingContext.CULL_FACE)
		gl.frontFace(WebGL2RenderingContext.CCW)
		gl.cullFace(WebGL2RenderingContext.BACK)
		gl.enable(WebGL2RenderingContext.DEPTH_TEST)
		gl.depthMask(true)
		gl.enable(WebGL2RenderingContext.BLEND)
		gl.blendFuncSeparate(
			WebGL2RenderingContext.SRC_ALPHA,
			WebGL2RenderingContext.ONE_MINUS_SRC_ALPHA,
			WebGL2RenderingContext.ONE,
			WebGL2RenderingContext.ONE)
	}

	public setBaseActivity(activity: ActivityBase): void {
		this.baseActivity = activity
		this.baseActivity.setEngine(this)
		this.baseActivity.start?.()
		this.baseActivity.addViewportNodes()
	}

	public run(): void {
		// Resumable: no-op while already running (a second rAF loop would
		// double the frame rate), otherwise (re)start the loop
		if (this.running && this.frameId !== 0) return
		this.running = true
		this.frameId = requestAnimationFrame(this.runLoop.bind(this))
	}

	public pause(): void {
		this.running = false
		cancelAnimationFrame(this.frameId)
		this.frameId = 0
	}

	/** Whether the frame loop is scheduled (false after pause() / destroy()) */
	public get isRunning(): boolean {
		return this.running && this.frameId !== 0
	}

	/** Draw a frame on the next tick regardless of idle throttling */
	public requestRender(): void {
		requestRender()
	}

	/**
	 * A media query that matches only the current device pixel ratio fires
	 * `change` when the window moves to a display with a different ratio or
	 * the page zooms; it is re-armed for the new ratio on each change
	 */
	private watchPixelRatio(): void {
		if(typeof window === "undefined" || typeof window.matchMedia !== "function") {
			return
		}
		this.pixelRatioQuery?.removeEventListener("change", this.pixelRatioChanged)
		this.pixelRatioQuery = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`)
		this.pixelRatioQuery.addEventListener("change", this.pixelRatioChanged)
	}

	public destroy(): void {
		this.pause()
		this.pixelRatioQuery?.removeEventListener("change", this.pixelRatioChanged)
		this.pixelRatioQuery = undefined
		this.baseActivity.destroy()
		this.world.destroy()
		this.labels.destroy()
		this.interface.destroy()

		this.context.destroy()
	}

	protected runLoop(_timestamp: DOMHighResTimeStamp): void {
		if(this.running) {
			this.frameId = requestAnimationFrame(this.runLoop.bind(this))

			// detect resize events before a frame render. The pixel ratio is
			// part of the check: it changes when the window moves between
			// monitors or the page zooms, with no change in CSS size
			const tickTime = performance.now() * 0.001
			if(this.context.canvasResized || tickTime - this.lastResizeCheck >= 1) {
				this.context.canvasResized = false
				this.lastResizeCheck = tickTime
				const bounds = this.context.canvas.getBoundingClientRect()
				const pixelRatio = this.targetPixelRatio(bounds.width, bounds.height)
				if(this.canvasWidth != bounds.width || this.canvasHeight != bounds.height || this.context.pixelRatio != pixelRatio) {
					this.resizeCanvas(bounds.width, bounds.height, pixelRatio)
				}
			}

			const frameId = this.frameCount
			this.context.frameTime = new time()
			// Resolve the latest hover position once per tick rather than per
			// pointer event (a full scene hit test each)
			this.context.gestures.flushHover()
			// Tasks that change something visible (uploads, node or transform
			// changes) request their own redraw; a timer merely firing is not
			// an event in itself since several are polling tasks
			this.taskManager.frame(frameId)

			// Focus/follow and camera matrices advance every tick so a
			// camera that starts moving while idle is noticed
			if(this.world.update()) {
				requestRender()
			}

			const now = performance.now() * 0.001
			const version = renderRequestVersion()
			if(version !== this.seenRequestVersion) {
				this.seenRequestVersion = version
				this.lastRequestTime = now
			}
			const active = now - this.lastRequestTime < this.idleDelay
			const idleInterval = this.idleFrameRate > 0 ? 1 / this.idleFrameRate : Infinity
			const draw = active || now - this.lastRenderTime >= idleInterval

			// render world, then labels (preserving world depth), then UI overlay
			if(draw && this.canvasWidth > 0 && this.canvasHeight > 0) {
				// Frame tasks and async loaders may have driven gl directly;
				// start the frame with no assumptions about driver state
				this.context.glState.invalidate()
				this.world.renderViewport()
				this.labels.renderViewport()
				this.interface.renderViewport()

				// Post-render hook: the drawing buffer is only valid until this
				// rAF callback returns (context is created without
				// preserveDrawingBuffer), so canvas captures must happen here
				this.onPostRender?.()
				this.lastRenderTime = now
				this.renderedFrames++
			}

			// calculate the frame rate every second
			this.frameCount++
			const end = performance.now() * 0.001
			if(end - this.lastTimestamp >= 1) {
				this.frameRate = this.renderedFrames - this.lastRenderedFrames
				this.lastRenderedFrames = this.renderedFrames
				this.lastTimestamp = end
			}
		}
	}

	/**
	 * Device pixels per CSS pixel to render at: the display ratio capped by
	 * context.maxPixelRatio and by the GPU's viewport limit (so a very large
	 * canvas on a HiDPI display degrades to a lower ratio instead of failing)
	 */
	protected targetPixelRatio(width: number, height: number): number {
		const display = (typeof window !== "undefined" && window.devicePixelRatio) || 1
		let ratio = Math.min(Math.max(display, 1), Math.max(this.context.maxPixelRatio, 1))
		const largest = Math.max(width, height)
		if(largest > 0 && largest * ratio > this.maxDrawingBufferDimension) {
			ratio = Math.max(1, this.maxDrawingBufferDimension / largest)
		}
		return ratio
	}

	private get maxDrawingBufferDimension(): number {
		if(this.maxDimensionCache === undefined) {
			const gl = this.context.gl
			const viewport = gl.getParameter(WebGL2RenderingContext.MAX_VIEWPORT_DIMS) as Int32Array
			const renderbuffer = gl.getParameter(WebGL2RenderingContext.MAX_RENDERBUFFER_SIZE) as number
			this.maxDimensionCache = Math.min(viewport[0], viewport[1], renderbuffer)
		}
		return this.maxDimensionCache
	}

	/**
	 * Size the drawing buffer. width/height are CSS pixels and remain the
	 * logical size for the viewports and cameras; the backing store is the
	 * CSS size times pixelRatio so one logical pixel maps to pixelRatio
	 * device pixels (gl.viewport covers the whole buffer, so NDC-based
	 * rendering scales for free)
	 */
	protected resizeCanvas(width: number, height: number, pixelRatio: number = this.context.pixelRatio): void {
		this.canvasWidth = width
		this.canvasHeight = height
		this.context.pixelRatio = pixelRatio
		this.context.gl.canvas.width = Math.round(width * pixelRatio)
		this.context.gl.canvas.height = Math.round(height * pixelRatio)
		requestRender()

		if(width > 0 && height > 0) {
			this.world.size(width, height)
			this.labels.size(width, height)
			this.interface.size(width, height)
		}
	}
}
